import { majorCardsList } from '#lib/domain/cards.ts';
import { MAX_HAND_CARDS, MAX_DEITY_CARDS } from '#lib/domain/constants.ts';
import type { MajorCardId } from '#lib/domain/types.ts';
import { createNewUniqueArray } from '#lib/utils/array.util.ts';
import { getAvailableCards, pickRandomCard, shuffleCards } from '../domain/card-dealer';
import type { MajorCardState } from '../domain/game.state.validation';

export type MajorCardsView = {
	readonly hasSavedThisRound: boolean;
	readonly hand: readonly MajorCardId[];
	readonly saved: readonly MajorCardId[];
	readonly discarded: readonly MajorCardId[];
	readonly reversed: readonly MajorCardId[];
	readonly numSavedCards: number;
	readonly remainingCards: number;
	readonly isCardReversed: (id: MajorCardId) => boolean;
	readonly isCardSaved: (id: MajorCardId) => boolean;
	readonly isCardDismissed: (id: MajorCardId) => boolean;
};

export class MajorArcanaState {
	public hasSavedThisRound = $state(false);
	public hand = $state<MajorCardId[]>([]);
	public saved = $state<MajorCardId[]>([]);
	public discarded = $state<MajorCardId[]>([]);
	public reversed = $state<MajorCardId[]>([]);

	public static get initialState(): MajorCardState {
		return { hasSavedThisRound: false, discarded: [], hand: [], reversed: [], saved: [] };
	}

	get numSavedCards() {
		return this.saved.length;
	}

	get remainingCards() {
		return this.availableCards.length;
	}

	get mayDrawCard() {
		return this.hand.length < MAX_HAND_CARDS && this.remainingCards > 0;
	}

	get hasMaxSavedCards() {
		return this.saved.length >= MAX_DEITY_CARDS;
	}

	reset = () => {
		this.restoreState(MajorArcanaState.initialState);
	};

	resetSaveStatus = () => {
		this.hasSavedThisRound = false;
	};

	restoreState = (state: MajorCardState) => {
		this.hand = [...state.hand];
		this.discarded = [...state.discarded];
		this.reversed = [...state.reversed];
		this.saved = [...state.saved];
		this.hasSavedThisRound = state.hasSavedThisRound;
	};

	toDTO = (): MajorCardState => {
		return {
			hasSavedThisRound: this.hasSavedThisRound,
			discarded: [...this.discarded],
			hand: [...this.hand],
			reversed: [...this.reversed],
			saved: [...this.saved]
		};
	};

	isCardReversed = (id: MajorCardId): boolean => {
		return this.reversed.includes(id);
	};

	isCardSaved = (id: MajorCardId): boolean => {
		return this.saved.includes(id);
	};

	isCardDismissed = (id: MajorCardId): boolean => {
		return this.discarded.includes(id);
	};

	saveInitialDeity = (id: MajorCardId) => {
		this.saved.push(id);
		this.hand = shuffleCards(this.availableCards).slice(0, MAX_HAND_CARDS);
	};

	save = (id: MajorCardId) => {
		if (this.hasMaxSavedCards) return;

		this.hand = this.hand.filter((cardId) => cardId !== id);
		this.saved = createNewUniqueArray(this.saved, id);
		this.hasSavedThisRound = true;
	};

	reverse = (id: MajorCardId) => {
		this.reversed = createNewUniqueArray(this.reversed, id);
	};

	discard = (id: MajorCardId) => {
		this.discarded = createNewUniqueArray(this.discarded, id);
		this.hand = this.hand.filter((cardId) => cardId !== id);
	};

	draw = () => {
		if (!this.mayDrawCard) return;

		const newCardId = pickRandomCard(this.availableCards);
		if (!newCardId) return;
		this.hand = createNewUniqueArray(this.hand, newCardId);
	};

	private get availableCards() {
		return getAvailableCards({
			cards: majorCardsList,
			excluded: [...this.hand, ...this.discarded, ...this.saved]
		});
	}
}
